import React from 'react';
import SurveyPage from '../components/SurveyPage';
import SurveyTitleArea from '../components/SurveyTitleArea';
import SurveySection from '../components/SurveySection';
import SurveyElement from '../components/SurveyElement';
import SurveyContentContainer from '../components/SurveyContentContainer';
import SurveyGridHeader from '../components/GridComponents/SurveyGridHeader';
import SurveyGridBody from '../components/GridComponents/SurveyGridBody';
import SurveyGridRow from '../components/GridComponents/SurveyGridRow';
import { getCurrencyPlaceHolder } from '../helpers';
import store from '../components/Stores/SurveyStore';
import {
  CommonResponses,
  ElementType,
  NumberFormat,
  SelectCombos,
  SelectCombosPABS,
  PlaceholderFormat,
} from '../Enums/constants';
import SurveyConditionalSegment from '../components/SurveyConditionalSegment';


export const PabsCostsTab = () => {
  let currencyPlaceholderThousand = getCurrencyPlaceHolder(PlaceholderFormat.CURRENCY_THOUSAND);
  let isOptionalTable = store.currentSurvey?.IsOptional ?? false;

  return (
    <SurveyPage>
      <SurveyTitleArea pageName="Costs" />
      <SurveyContentContainer>
        <SurveySection labelText="Costs">
          <SurveyElement
            questionNumber="9"
            labelText={`Complete the following table below (${currencyPlaceholderThousand})`}
            inputType=""
            name=""
          />
          <SurveyElement
            questionNumber="9.a"
            labelText="Total administrative expenses per your financial statements (ACFR in the U.S.)"
            inputType={ElementType.NUMBER}
            format={NumberFormat.DECIMAL1}
            name="Q_25"
            subQuestion
          />
          <SurveyElement
            labelText="Minus, if included in administrative expenses:"
            inputType=""
            name=""
          />
          <SurveyElement
            questionNumber="9.b"
            labelText="Investment administration costs"
            inputType={ElementType.NUMBER}
            format={NumberFormat.DECIMAL1}
            name="InvestmentCosts"
            subQuestion
          />
          <SurveyElement
            questionNumber="9.c"
            labelText="Accrued, non-cash, pension and OPEB expense (per U.S. GASB 68 & 75, if applicable)"
            inputType={ElementType.NUMBER}
            helpText="<b>Accrued Pension and OPEB expenses</b><br/>If you are a U.S. system, accrued pension and OPEB expenses, as per GASB 68 &amp; 75, respectively, reported in the year."
            format={NumberFormat.DECIMAL1}
            name="AccruedPensionOPEBCosts"
            subQuestion
            allowUnknown
          />
          <SurveyElement
            labelText="Plus, if not included in administrative expenses:"
            inputType=""
            name=""
          />
          <SurveyElement
            questionNumber="9.d"
            labelText="Cash contributions for pension and OPEB, for active staff"
            inputType={ElementType.NUMBER}
            helpText="<strong>Cash Pension and OPEB contributions for active staff</strong><br /><div>Actual cash contributions for pension and other post employment benefits (OPEB) such as healthcare paid in the year for administrative employees. Exclude amounts that relate to investment and retired staff.</div>"
            format={NumberFormat.DECIMAL1}
            name="CashPensionOPEBCosts"
            subQuestion
            allowUnknown
          />
          <SurveyElement
            questionNumber="9.e"
            labelText="Pay-as-you-go benefits for retired staff"
            inputType={ElementType.NUMBER}
            helpText="<strong>Pay-as-you-go Benefits for Retired Staff</strong><br />All pension and other post employment benefits (OPEB) such as healthcare paid in the year for retired administrative staff. Exclude amounts that relate to retired investment staff."
            format={NumberFormat.DECIMAL1}
            name="SalariesPayAsYouGo2"
            subQuestion
            allowUnknown
          />
          <SurveyElement
            questionNumber="9.f"
            labelText="Amortization and depreciation of administrative assets"
            inputType={ElementType.NUMBER}
            format={NumberFormat.DECIMAL1}
            name="SACRSDepnAmt"
            subQuestion
          />
          <SurveyElement
            questionNumber="9.g"
            labelText="Actuarial fees and costs"
            inputType={ElementType.NUMBER}
            name="SACRSActuarialAmt"
            format={NumberFormat.DECIMAL1}
            subQuestion
          />
          <SurveyElement
            questionNumber="9.h"
            name="OtherAdministrationAmt"
            labelText="Other costs and professional fees relating to pension administration"
            format={NumberFormat.DECIMAL1}
            inputType={ElementType.NUMBER}
            subQuestion
          />
          <SurveyElement
            name="totalBenefitCost"
            labelText="Total benefit administration expenses"
            formula="Q_25 - InvestmentCosts - AccruedPensionOPEBCosts + CashPensionOPEBCosts + SalariesPayAsYouGo2 + SACRSDepnAmt + SACRSActuarialAmt + OtherAdministrationAmt"
            format={NumberFormat.DECIMAL1}
            inputType={ElementType.CALCULATED}
            note="to be attributed in the Cost and Staff by Activity table below"
          />
        </SurveySection>
        <SurveySection labelText="Costs and Staff by Activity">
          <SurveyElement
            questionNumber="10"
            labelText="Attribute your total benefit administration expenses (per the total at the bottom of the question above) and staff 
                            to the activities listed in the table below.<ul>
                            <li>Instructions for each column can be viewed by pointing at the help bubbles in the column headers.</li>
                            <li>Definitions of each activity can be viewed by pointing at the help bubbles next to each activity name.</li></ul>"
          />
          <SurveyElement
            inputType="grid"
            styleName="dataTable pabsCostTable"
          >
            <SurveyGridHeader customLayout={true}>
              {/*Row #1: grouped heading*/}
              <tr>
                <th rowSpan={2}>
                  <SurveyElement
                    labelText="Activity"
                    inputType={ElementType.LABEL}
                  />
                </th>
                <th rowSpan={2}>
                  <SurveyElement
                    labelText='Direct Full Time Equivalent Internal Staff (# of FTE)'
                    inputType={ElementType.LABEL}
                    tooltip='left'
                    helpText="<b> Instructions for this column - Direct full time equivalent (FTE) staff</b> 
                                          <br><br> Attribute 100% of your administrative staff (FTE) to the activities.<ul> 
                                          <li> The average number of FTE employed during the fiscal year is preferred to end of year FTE because then the FTE will more closely correspond to the salaries paid. 
                                          <li>Exclude FTE related to unfilled positions or to investment management and investment administration and support. 
                                          <li>Include FTE who are under contract, part-time and non-permanent. For example, a person who works 3 days a week counts as 0.6 FTE (i.e., 3 days / 5 days per workweek).   
                                          <li> Do not reduce FTE for furlough days and vacation.  </ul> 
                                          For each activity listed, include: <ul> 
                                          <li>All staff that <u>directly</u> perform the activity. 
                                          <li>Related support and management staff that are in the same department. For example, for the contact centre include call and email customer service agents, call centre trainers, call centre analysts and call centre managers. </ul>
                                          <br> If a person's time is split between multiple activities then split his/her FTE per the approximate time spent on each activity. For example, if a person spends approximately 60% of their time on counseling and 40% on presentations then attribute 0.6 FTE to counseling and 0.4 FTE to presentations. 
                                          <br><br> Detailed activity definitions can be viewed by pointing your cursor at each activity in the first column of this table."
                  />
                </th>
                <th colSpan={3}>
                  <SurveyElement
                    labelText="Direct Costs ($000s)"
                    inputType={ElementType.LABEL}
                  />
                </th>
                <th colSpan={4} hidden={!isOptionalTable}>
                  <SurveyElement
                    labelText="OPTIONAL: Attribute activity 5 - 7 costs to activities 1- 4 ($000s)"
                    inputType={ElementType.LABEL}
                  />
                </th>
              </tr>

              {/* Row #2: the detail columns under that group */}
              <tr>
                <th>
                  <SurveyElement
                    labelText='Staff Salaries & Benefits ($000s)'
                    inputType={ElementType.LABEL}
                    helpText='<span> Instructions for this column - Staff salaries and benefits</span> <br/><br/> 
                                          <span>Supply the staff costs for the full time equivalent staff identified in the first column.</span> <br/><br/> 
                                          <span>Staff costs include compensation (salary, wages, overtime, bonuses), benefits (cars, pensions) and National Insurance.</span>'
                  />
                </th>
                <th>
                  <SurveyElement
                    labelText='Third Party Fees and Other Direct Costs ($000s)'
                    inputType={ElementType.LABEL}
                    helpText='<span> Instructions for this column - Third-Party Fees & Other Direct Costs</span>  <br/><br/><span>Include here all the other non-staff costs and fees that can be directly attributed to the specific function but that are not already included in the other functions listed. (e.g., do not include accommodation costs except in the accommodation line) These may include, but are not limited to:</span><br/> <br/><span>• Consulting fees for projects attributable to the activity.</span><br/> <span>• Subscriptions, conferences, activity specific training.</span><br/> <span>• Office supplies and stationery.</span><br/> <span>• Travel.</span>'
                  />
                </th>
                <th>
                  <SurveyElement
                    labelText='Total'
                    inputType={ElementType.LABEL}
                    helpText="The sum of this 'Total' column should equal the total at the bottom of question 9 above."
                  />
                </th>
                <th hidden={!isOptionalTable}>
                  <SurveyElement
                    labelText='5 a-d Major Projects'
                    inputType={ElementType.LABEL}
                    helpText="<span> Instructions for this column - Staff salaries and benefits</span> <br/><br/> 
                                            <span>Supply the staff costs for the full time equivalent staff identified in the first column.</span> <br/><br/> 
                                            <b>Instructions for this column - 5 Major Projects</b> <br/><br/>  
                                            <b><i>Completing this column is optional.</i> </b>
                                            Attribute 100% of the sum of:<ul> 
                                            <li>5a Amortization of Capitalized Non-IT Projects 
                                            <li>5b Non-IT Major Projects (if you don't capitalize)   
                                            <li>5c Amortization of Capitalized IT Projects <li>5d IT Major Projects (if you don't capitalize) </ul>
                                            to the activities that benefit most materially from the major projects. For example: <ul> 
                                            <li>A pension payroll system should be attributed to '1a Pension Payments' and/or '3c Data not from Employers'. 
                                            <li>A customer relationship management interface should be attributed to the contact center. 
                                            <li>A website developed for members and employers should be attributed between '2f Mass Communication' and '3b Service to Employers'. 
                                            <li>A workflow management system should be attributed between '1a-f Member Transactions', '2e Written Pension Estimates' and '3c Data not from Employers'.                                                                                                                                                                                                                      
                                            </ul>When the project being amortized applies to multiple activities, such as a workflow management system, costs can be attributed pro rata between the activities based on FTE or Total Direct Costs.
                                            <span>Staff costs include compensation (salary, wages, overtime, bonuses), benefits (cars, pensions) and National Insurance.</span>"
                  />
                </th>
                <th hidden={!isOptionalTable}>
                  <SurveyElement
                    labelText='6a IT Strategy DB Mgmt Applications'
                    inputType={ElementType.LABEL}
                    helpText="<b>Instructions for this column - 6a IT Strategy</b> <br/><br/> 
                                            <b><i>Completing this column is optional.</i> 
                                            </b> Attribute 100% of the Total direct cost of '6a. IT Strategy, Database Management, Applications (excluding Major Projects)'
                                            to the activities that most materially benefit. For example, the cost of maintaining or enhancing the member website should be 
                                            attributed to '2f Mass Communication'. When the project applies to multiple activities, such as a workflow management system, 
                                            costs can be attributed pro rata between the activities based on FTE or Total Direct Costs."
                  />
                </th>
                <th hidden={!isOptionalTable}>
                  <SurveyElement
                    labelText='6b IT Desktop + 7a - g Support Services'
                    inputType={ElementType.LABEL}
                    helpText="<b>Instructions for this column - 6b, 7a - g</b> <br/> <br> 
                                            <b><i>Completing this column is optional.</i> 
                                            </b> Attribute 100% of the costs of activities 6b IT Desktop, Networks, Telecom, 
                                            and 7a - g Support Services and Other to activities 1 through 4 based on usage. "
                  />
                </th>
                <th hidden={!isOptionalTable}>
                  <SurveyElement
                    labelText='Total'
                    inputType={ElementType.LABEL}
                  />
                </th>
              </tr>
            </SurveyGridHeader>
            <SurveyGridBody isCompact={true} tableName="pabsCostTable">
              <SurveyGridRow styleName='noHoverEffect'>
                <SurveyElement
                  labelText='<b>1. Member Transactions</b>'
                  inputType={ElementType.LABEL}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.1.a'
                  labelText='Pension Payments'
                  inputType={ElementType.LABEL}
                  helpText="<b>Pension Payments</b> <br><br> 
                                        1.  Paying (but not incepting) annuity pensions for disability, early and normal retirees and their survivors, including: <br/> <ul> 
                                        <li>EFT processing. </li> 
                                        <li>Check processing and postage. </li> 
                                        <li>Resolving failed, misdirected and lost payments. </li> 
                                        <li>Paying, but not calculating, adjustments to pensions. For example, many systems pay retroactive adjustments to pensions initiated based on incomplete data. 
                                        <li>Collecting overpayments. </li> 
                                        <li>The processing of return payments from annuitants. </li> 
                                        <li>Stopping pension payments upon  the death of an annuitant. </li> </ul>
                                        <br>
                                        2. Processing deductions from the gross pension payment. For example: <br/> <ul> 
                                        <li>Processing and paying taxes and other governmental deductions, such as social security. </li> 
                                        <li>Processing legally required deductions such as liens on wages ordered by a judge. </li> 
                                        <li>Processing and effecting payment of optional deductions that the member can request, such as for health care or optional life insurance or union dues. </li> 
                                        <li>Processing and mailing of check stubs or EFT payment advices. </li> 
                                        <li>Processing and mailing of annual tax receipts to annuitants. </li> 
                                        <li>Keeping track of the rules and regulations for deductions. </li> </ul>  
                                        <br>
                                        3. Confirming payment eligibility. This includes: <br/>  <ul> 
                                        <li>Cross referencing pension payrolls with death records. </li> 
                                        <li>Obtaining proof that someone is alive. </li> 
                                        <li>Checking eligibility. </li> </ul><br/>  
                                        If a sister organization performs any of the above tasks, then the costs incurred by the sister organization should be included here and in your Total Costs. <br/><br/>  
                                        Do NOT include: <br/>  <ul> 
                                        <li>Maintaining banking, address and beneficiary data for retirees. These belong in 3c Data Not from Employers. </li> 
                                        <li>The work of inflation adjustments. It belongs in 1b Pension Inceptions. </li> </ul><br/>"
                />
                <SurveyElement
                  name='FTEPaying'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesPaying'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdPaying'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='total1'
                  formula='SalariesPaying + ThirdPaying'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinPaying'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinPaying'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinPaying'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                  formula='MPinPaying + ITinPaying + OtherinPaying'
                  name='calculatedField8'
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.1.b'
                  labelText='Pension Inceptions and Written Pension Estimates'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Pension Inceptions and Written Pension Estimates</strong><br/><br/>
                                        1. New Annuitants: calculating, finalizing and arranging annuity pensions to new payees (i.e., early and normal service retirements, 
                                        death in-service or on-pension resulting in annuity payments to spouse or dependents, divorce that results in new annuity pension streams).
\                                       Exclude disability inceptions (except Dutch systems). Disability belongs in 1e Disability. <br/><br/>
                                        2. Changing the gross amount of pensions paid to existing pensioners (excluding disability pensions) including: <br/><ul>
                                        <li>Adjusting pensions that were initiated based on incomplete or estimated data.</li>
                                        <li>Reducing the annuity payment when pensioner begins receiving social security (NL: AOW, Canada: CPP).</li>
                                        <li>Cost of living adjustments for pensioners.</li>
                                        <li>Last survivor options that reduce the amount paid to the pensioner when a spouse dies.</li></ul><br>
                                        3. Time spent by the pension inception team on appeals about annuity pension inceptions. Exclude time and cost of legal staff or external 
                                        legal counsel. Legal costs belong in 7d Legal and Rule Interpretation.<br/><br/>
                                        4. Preparing and sending, by mail or email, customized written estimates in response to requests from individual members regarding:<br/><ul>
                                        <li>Annuity pensions including early and service retirement.</li>
                                        <li>Giving customized written insight into pension consequences of divorce, death, layoffs, taxes, gaps in pension coverage, etc.</li>
                                        <li>Lump sum or commuted value payouts.</li></ul>"
                />
                <SurveyElement
                  name='FTEInceptions_InclEstimates'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesInceptions_InclEstimates'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdInceptions_InclEstimates'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField46'
                  formula='SalariesInceptions_InclEstimates + ThirdInceptions_InclEstimates'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='MPinInceptions'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='ITinInceptions'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='OtherinInceptions'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField9_'
                  formula='MPinInceptions + ITinInceptions + OtherinInceptions'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.1.c'
                  labelText='Withdrawals, Refunds, Transfers-out'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Withdrawals, Refunds, Transfers Out</strong><br/><br/>
                                        Payments that terminate your relationship with a member including:<br/><ul>
                                        <li>Withdrawals and Refunds of contributions.</li>
                                        <li>Lump sum and commuted value payments.</li>
                                        <li>Pre-retirement death that results in a final payment (refund, commuted value, death payment).</li>
                                        <li>One time death payments that are supplemental to the annuity payments. Do not include the cost of paying or stopping the final pension payment which belong in 1a Pension Payments.</li>
                                        <li>Hardship withdrawals (including partials).</li>
                                        <li>Excess contribution refunds (For example, 50% Rule Refunds in Canada).</li>
                                        <li>Individual rollovers of DB monies to internal DC accounts which effectively terminate the DB pension entitlement.</li>
                                        <li>Individual transfers-out of monies to authorized external systems.</li>
                                        <li>Collective transfers-out when an employer exits your system.</li></ul><br/>
                                        The activity also includes:<br/><ul>
                                        <li>Providing written estimates of refunds and transfers-out (do not include estimates for Lump Sums and Commuted Value Payments which belong in 1b. Pension Inceptions and Written Estimates).</li>
                                        <li>Calculating, arranging and making estimated and final payments.</li>
                                        <li>Related tax deductions, filings and reporting sent to members and tax authorities.</li>
                                        <li>Reminders to speed up processing by external parties.</li>
                                        <li>Collecting overpayments of lump sums or other terminating payments.</li></ul>"
                />
                <SurveyElement
                  name='FTERefunds'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesRefunds'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdRefunds'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField3'
                  formula='SalariesRefunds + ThirdRefunds'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='MPinRefunds'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='ITinRefunds'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='OtherinRefunds'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField10'
                  formula='MPinRefunds + ITinRefunds + OtherinRefunds'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.1.d'
                  labelText='Purchases and Transfers-in'
                  inputType={ElementType.LABEL}
                  helpText="<b>Purchases and Transfers-in</b><br><br><ul>
                                        <li>Service credit purchases such as purchases of refunded past service, military service, etc.</li> 
                                        <li>Purchases that provide members with additional pensionable salary.</li> 
                                        <li>Individual transfers-in/ rollover of monies from external retirement systems.</li> 
                                        <li>Collective transfers-in such as when a new employer is merged into your system.</li></ul><br>
                                        The activity includes:<br/><ul> 
                                        <li>Providing formal written estimates of purchases and transfers-in/ rollovers.</li> 
                                        <li>Posting purchases and applying payments.</li> <li>Receiving purchase payments.</li> 
                                        <li>Related tax reporting sent to members and tax authorities.</li> 
                                        <li>Purchases applicant dealing directly with the Purchase division, subsequent to an initial request for a Purchase. </li></ul>"
                />
                <SurveyElement
                  name='FTEPurchases'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesPurchases'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdPurchases'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField4'
                  formula='SalariesPurchases + ThirdPurchases'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='MPinPurchases'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='ITinPurchases'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='OtherinPurchases'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField11'
                  formula='MPinPurchases + ITinPurchases + OtherinPurchases'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.1.e'
                  labelText='Disability'
                  inputType={ElementType.LABEL}
                  helpText="<b>Disability</b><br><br>
                                        Include only if you are responsible for the administration of non-optional long term disability, and/ or short term disability. Includes: <br/><ul> 
                                        <li>Written disability estimates.</li> 
                                        <li>Disability approval process.</li> 
                                        <li>Applications and inceptions.</li> 
                                        <li>Disability appeals and complaints, provided that they are not handled by your legal staff or external legal counsel. 
                                        Legal costs belong in 7d Legal and Rule Interpretation.</li> 
                                        <li>Monitoring and reviewing disabled pensioners and/ or temporarily disabled members for continuing entitlement, 
                                        including checks on income and medical reviews.</li> <li>Checks on eligibility of disability payments.</li> 
                                        <li>Rehabilitation of disability recipients (very few systems perform this activity).</li>
                                        <li>Medical assessment reviews of new hires for eligibility for death and disability benefits.</li> </ul> <br>
                                        Do not include if disability is an optional benefit for members. Instead include in 1g. Do not include insurance costs. 
                                        If an insurance company administers the disability program on your behalf, and you cannot clearly separate the administration 
                                        cost from the insurance cost, then treat disability as a third party administered benefit. Do not include paying disability pensions. 
                                        This belongs in 1a Pension Payments. <br/></ul>"
                />
                <SurveyElement
                  name='FTEDisabilityB'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesDisabilityB'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdDisabilityB'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField5'
                  formula='SalariesDisabilityB + ThirdDisabilityB'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='MPinDisabilityB'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='ITinDisabilityB'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='OtherinDisabilityB'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField12'
                  formula='MPinDisabilityB + ITinDisabilityB + OtherinDisabilityB'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.1.f'
                  labelText='Healthcare Administration'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Healthcare Administration</strong><br/><br>
                                        This activity only applies to the subset of U.S. systems that administer healthcare. Include only the costs of the healthcare 
                                        department and any healthcare activities that are not closely integrated with pension administration activities 
                                        (i.e., If the healthcare call center is separate from the pension administration call center, include its costs and volumes here. 
                                        But if healthcare calls are integrated with the pension call center, include the costs and volumes in the Call Center activity.). 
                                        The healthcare department will usually, at a minimum, be responsible for design, vendor management and enrolment.<br/><br>
                                        Do NOT include any activities that are integrated with pension administration and not done by the healthcare department. For example:<br/> 
                                        <li>Communication activities such as call center calls and presentations are often integrated with pension administration. 
                                        In these cases the cost and volumes belong in the pension administration activity.</li> 
                                        <li>Similarly, the legal and actuarial departments often handle both healthcare and pension issues. 
                                        If this is the case, these costs belong in activities 7c Actuarial and 7d Legal and Rule Interpretation.</li></ul>"
                />
                <SurveyElement
                  name='FTEHealthcare'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesHealthcare'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdHealthcare'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField6'
                  formula='SalariesHealthcare + ThirdHealthcare'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='MPinHealthcare'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='ITinHealthcare'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='OtherinHealthcare'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField13'
                  formula='MPinHealthcare + ITinHealthcare + OtherinHealthcare'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.1.g'
                  labelText='Optional and Third Party Administered Benefits'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Optional and Third-Party Administered Benefits</strong><br/><br>
                                        Examples of optional and third party administered benefits are:<ul>
                                        <li>Benefits that are supplemental to the members' primary pension benefit at retirement. 
                                        Examples include tax deferred savings plans (i.e., 403B, 457, 401k and 401a), insurance (dental, vision, long-term care) 
                                        and loans to members.</li> 
                                        <li>Third party administered benefits. For example, the defined contribution portion of hybrid DB/DC plans are often outsourced.</li> </ul> 
                                        Include the costs of:</br> <ul> 
                                        <li>Specialists responsible for optional and third party benefits, including design, vendor management and enrolment.</li> 
                                        <li>Fees paid to third parties to administer outsourced benefits.</li> </ul> 
                                        Do NOT include:<ul> 
                                        <li>Communication activities such as call center calls, presentations and brochures. 
                                        These belong in the communication activities. 
                                        For example, the cost and volumes of call center calls about supplemental benefits belong in 2a Contact Center.</li> 
                                        <li>Actuarial and legal costs. These belong in activities 7c Actuarial and 7d Legal and Rule Interpretation.</li>"
                />
                <SurveyElement
                  name='FTEOptional'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesOptional'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdOptional'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField7'
                  formula='SalariesOptional + ThirdOptional'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='MPinOptional'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='ITinOptional'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='OtherinOptional'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField144'
                  formula='MPinOptional + ITinOptional + OtherinOptional'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow styleName='noHoverEffect'>
                <SurveyElement
                  labelText='<b>2. Member Communication</b>'
                  inputType={ElementType.LABEL}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.2.a'
                  labelText='Contact Center'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Contact Center</strong> <br/><br/>
                                        The 'first-line' communication work for active, inactive and annuitant member inquiries. 
                                        First-line communication includes responding to general questions, initial requests for activity specific work to be performed 
                                        (such as a request for a Written Estimate or a Refund of Contributions, death reporting, change of address or direct deposit or beneficiary), 
                                        questions about account status or annual statements, advice given over the phone, etc. 
                                        This activity includes member inquiries by:<ul>
                                        <li>Telephone: waiting for calls, talking to members on the phone, redirecting calls, training Contact Center staff, 
                                        auditing calls, call satisfaction surveying and long distance charges.</li>
                                        <li>Automated information or self-serve lines (i.e., telephone lines where the member never needs to speak to a 
                                        service representative and can navigate menu options where they request forms and publications, etc.). 
                                        But do not include the cost of responding to requests for forms and publications. 
                                        This belongs in Activity 2e Mass Communication.</li>
                                        <li>Email: reading, responding to simple requests, redirecting activity specific requests.</li>
                                        <li>Written correspondence: reading, responding to simple requests, redirecting activity specific requests.</li></ul>
                                        Includes contact center hardware and software. 
                                        Do NOT include:<ul>
                                        <li>Work performed after the call for activities where costs are collected separately. 
                                        For example, if a member requests a written pension or benefit estimate, then the cost of preparing it belongs in 
                                        Activity 1b Pension Inceptions and Written Estimates and not here, even if the work is done in the Contact Center.</li>
                                        <li>Subsequent follow-up activity specific communication. For example, a disability applicant dealing directly with the disability division belongs in 1e Disability.</li></ul>"
                />
                <SurveyElement
                  name='FTECalls'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesCalls'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdCalls'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField14'
                  formula='SalariesCalls + ThirdCalls'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinCalls'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinCalls'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinCalls'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField20'
                  formula='MPinCalls + ITinCalls + OtherinCalls'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.2.b'
                  labelText='Mail Room, Imaging'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Mail Room, Imaging</strong><br/><br/><ul> 
                                        <li>Managing incoming and outgoing mail.</li> 
                                        <li>Incoming written correspondence: sorting, redirecting activity specific requests.</li> 
                                        <li>Scanning and imaging of incoming documents and forms to start a workflow process.</li> </ul>  
                                        Do NOT include: </br><ul> 
                                        <li> Postage. This belongs in the activities where they were incurred. 
                                        For example, the mailing of pension checks, check stubs, EFT advices or annual tax receipts belong in 1a Pension Payments.</li>"
                />
                <SurveyElement
                  name='FTEMail'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesMail'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdMail'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField15'
                  formula='SalariesMail + ThirdMail'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinMail'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinMail'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinMail'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField21'
                  formula='MPinMail + ITinMail + OtherinMail'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.2.c'
                  labelText='1-on-1 Counseling'
                  inputType={ElementType.LABEL}
                  helpText="<strong>1-on-1 Counseling</strong><br/><br/><ul> 
                            <li>Walk-in traffic that meets with counselors.</li> 
                            <li>Pre-scheduled 1-on-1 retirement and other counseling. 
                                Include pre-scheduled counseling sessions that are conducted via teleconference or 
                                over the phone that cover materials sent to the member in advance of the session</li> 
                            <li>If written pension estimates are prepared during a counseling session the cost belongs
                                in counseling and not in pension inceptions and written estimates.</li>"
                />
                <SurveyElement
                  name='FTECounseling1on1'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesCounseling1on1'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdCounseling1on1'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField16'
                  formula='SalariesCounseling1on1 + ThirdCounseling1on1'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinCounseling1on1'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinCounseling1on1'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinCounseling1on1'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField22'
                  formula='MPinCounseling1on1 + ITinCounseling1on1 + OtherinCounseling1on1'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.2.d'
                  labelText='Member Presentations'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Member Presentations</strong><br/><ul>
                                        <li>All presentations to members and retirees.</li>
                                        <li>Group retirement counseling.</li>
                                        <li>Webinars.</li></ul>"
                />
                <SurveyElement
                  name='FTECounselingGroup'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesCounselingGroup'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdCounselingGroup'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField17'
                  formula='SalariesCounselingGroup + ThirdCounselingGroup'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinCounselingGroup'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinCounselingGroup'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinCounselingGroup'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField23'
                  formula='MPinCounselingGroup + ITinCounselingGroup + OtherinCounselingGroup'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.2.e'
                  labelText='Mass Communication'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Mass Communication</strong><br/><br/>
                                        Any benefit related communication that is sent to all members or groups of members. 
                                        The cost should include design, printing, and mailing costs. Examples include:<br/><ul>
                                        <li>Member Statements.</li><li>Brochures and publications.</li>
                                        <li>Newsletters and Information Letters.</li>
                                        <li>Videos, CD-ROMS.</li>
                                        <li>Website pension content targeted at members and annuitants. Includes design, development and maintenance of the member 
                                        content as well as its pro rata share of the website infrastructure.</li>
                                        <li>Annual reports and popular annual report summaries, but do not include the accounting and auditing costs incurred 
                                        to prepare the annual report. Accounting costs belong in 4a Financial Administration and Control and auditing costs belong in 7e Audit.</li>
                                        <li>Welcome kits.</li>
                                        <li>Letter informing members when they reach or approach certain milestones such as becoming vested, eligible for disability, eligibility for retirement, etc.</li></ul>
                                        Do NOT include:<br /><ul>
                                        <li>Pre-scheduled retirement counseling done in group sessions or 1-on-1 or member presentations. These belong in 2c and 2d.</li>
                                        <li>1-on-1 correspondence. These costs belong in the activity to which the correspondence pertains. 
                                        For example, correspondence re: pension estimates belongs in 1b Pension Inceptions and Written Estimates.</li>
                                        <li>Payment advices, check stubs, letters informing of changes in gross amount, and annual tax receipts prepared for retirees. These belong in 1a Pension Payments.</li>
                                        <li>Employer targeted communication such as the employer portions of the website and employer newsletters. These belong in 3b Service to Employers.</li></ul>"
                />
                <SurveyElement
                  name='FTECommunication'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesCommunication'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdCommunication'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField19'
                  formula='SalariesCommunication + ThirdCommunication'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinCommunication'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinCommunication'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinCommunication'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField25'
                  formula='MPinCommunication + ITinCommunication + OtherinCommunication'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow styleName='noHoverEffect'>
                <SurveyElement
                  labelText='<b>3. Collections and Data Maintenance</b>'
                  inputType={ElementType.LABEL}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.3.a'
                  labelText='Data and Money from Employers'
                  inputType={ElementType.LABEL}
                  helpText="<b>Data and Money from Employers</b><br><br><ul>
                                        <li>Collection and cashiering of member and employer contributions from employers. 
                                        <li>Collection of member data (service credit, salary, personal information, employer, etc) from employers.</li> 
                                        <li>Reconciliation of required versus remitted money.</li> 
                                        <li>Analysis, correction and confirmation with employer of member data. 
                                        <li>Setting up the data and money collection process for new employers. 
                                        <li>Improving the data collection process for existing employers 
                                        (i.e., converting paper systems to electronic, etc).</li>  
                                        <li>Registering member choices and instructions that are received through the employer. For example:</li> 
                                        <li>Some hybrid DB/ DC systems require that a member's DC instructions come through the employer.</li> 
                                        <li>Members at some Dutch systems can choose to contribute more to get early retirement. 
                                        This choice is registered via the employer.</li> <br>
                                        (The activities below used to be in a separate activity 'Billing and Inspection' and are now merged into 3a Data and Money from Employers)<br/> 
                                        <li>Advising employers of the required contribution rate 
                                        (but excluding the actuarial cost of determining the rates which belongs in 7c Actuarial).</li> 
                                        <li>Billing employers for regular contributions, special contributions to cover funding shortfalls, 
                                        additional payments to retirees funded by the employer, etc.</li> 
                                        <li>Collecting bad debts. But do not include legal costs. These belong in 7d Legal.</li> 
                                        <li>Employer reviews or audits. For example, several systems perform on-site reviews of 
                                        employers that have problems providing data and or contributions on a timely basis. 
                                        They want to ensure that the employers are correctly fulfilling their obligations to their members.</li> 
                                        <li>Inspection and enforcement of obligation to participate in the System 
                                        (i.e., participation in some Dutch industry funds is mandatory if the employer has certain characteristics).</li></ul>"
                />
                <SurveyElement
                  name='FTEDataAndMoney'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesDataAndMoney'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdDataAndMoney'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField26'
                  formula='SalariesDataAndMoney + ThirdDataAndMoney'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinDataAndMoney'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinDataAndMoney'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinDataAndMoney'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField29'
                  formula='MPinDataAndMoney + ITinDataAndMoney + OtherinDataAndMoney'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.3.b'
                  labelText='Service to Employers'
                  inputType={ElementType.LABEL}
                  helpText="<b>Service to Employers</b><br><br><ul>
                                        <li>Training employers.</li>
                                        <li>Helping new employers.</li>
                                        <li>Maintaining employer relationships.</li>
                                        <li>Presentations, counseling, workshops for employers.</li>
                                        <li>Publications and newsletters for employers (as opposed to materials sent to members through employers).</li>
                                        <li>Employer helpdesk/ Employer Call Center.</li> 
                                        <li>Employer website including the design, development and maintenance as well as the pro rata share of the website infrastructure.</li> 
                                        <li>Advice, account management, HRM support re: pensions</li>  <br>
                                        Do NOT include marketing to employers.  Marketing costs belong in 4c Government and Public Relations. <br/>"
                />
                <SurveyElement
                  name='FTEEmployers'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesEmployers'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdEmployers'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField27'
                  formula='SalariesEmployers + ThirdEmployers'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinEmployers'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinEmployers'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinEmployers'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField30'
                  formula='MPinEmployers + ITinEmployers + OtherinEmployers'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.3.c'
                  labelText='Data Not from Employers'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Data not from Employers</strong><br/><br/>
                                        Gathering and maintaining member data that is not provided by employers including:<ul><ul>
                                        <li>Retiree and Inactive data maintenance such as change of address, change of beneficiary, 
                                        change in marital status, death, registering changes in payments, etc.</li>
                                        <li>Tracking 'lost' inactive members.</li>
                                        <li>If applicable, registering cost of living adjustments to the salary base of inactive members 
                                        (a few systems do this).</li>
                                        <li>Registering the impact of divorce on the future rights of members. 
                                        But do not include the work of estimating the impact of divorce which belongs in either 
                                        1b Pension Inceptions and Written Estimates, 2c 1-on-1 Counseling and or 2a Call Center. 
                                        Also do not include the cost of changing or initiating pensions as a result of divorce. T
                                        his belongs in 1b Pension Inceptions.</li>
                                        <li>Billing and collecting contributions directly from inactive members. 
                                        For example, nurses in the Netherlands can continue to contribute to their pension even if they are inactive.</li>
                                        <li>Registering member choices and instructions that are received directly from the member. For example:</li>
                                        <li>Defined Contribution instructions obtained directly from active, inactive or retired members, such as changes in asset allocation.</li></ul></ul><br/><ul><ul>
                                        Do NOT include the cost of incepting new pensions or determining changes to the gross amount 
                                        of the pension paid to retirees. This belongs in 1b Pension Inceptions and Written Estimates.</ul></ul>"
                />
                <SurveyElement
                  name='FTEDataMembers'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesDataMembers'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdDataMembers'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField28'
                  formula='SalariesDataMembers + ThirdDataMembers'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinDataMembers'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinDataMembers'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinDataMembers'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField31'
                  formula='MPinDataMembers + ITinDataMembers + OtherinDataMembers'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow styleName='noHoverEffect'>
                <SurveyElement
                  labelText='<b>4. Governance and Financial Control</b>'
                  inputType={ElementType.LABEL}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.4.a'
                  labelText='Financial Administration and Control'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Financial Administration and Control</strong><ul>
                                        <li>Budgeting and forecasting.</li>
                                        <li>Financial reporting including preparing the annual report.</li>
                                        <li>The chief financial officer's office including assistants. Exclude time spent on investments.</li>
                                        <li>Compliance unless it is done by the legal department. If this is the case compliance costs belong in 7d Legal and Rules Interpretation.</li>
                                        <li>Risk.</li></ul><br />Do NOT include:<br /><ul>
                                        <li>Internal auditing of activity processes, or auditing of financial statements. These belong in 7e Auditing.</li>
                                        <li>Actuarial work. This belongs in 7c Actuarial.</li><li>Printing and mailing, including postage, of annual reports to members. This goes in 2e Mass Communication .</li>
                                        <li>Work that pertains to the Investment division, such as investment accounting.</li></ul>"
                />
                <SurveyElement
                  name='FTEFinancial'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesFinancial'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdFinancial'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField32'
                  formula='SalariesFinancial + ThirdFinancial'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinFinancial'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinFinancial'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinFinancial'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField35'
                  formula='MPinFinancial + ITinFinancial + OtherinFinancial'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.4.b'
                  labelText='Board, Strategy, Policy'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Board, Strategy, Policy</strong> </br></br><ul>
                                        <li>Board of Trustees: elections, fees, expenses, etc.</li>
                                        <li>The CEO's office including assistants. Exclude time spent on investments (when in doubt we suggest 50% administration, 50% investments).</li>
                                        <li>Benchmarking studies, strategic planning, fiduciary audits, asset liability studies. But exclude portions attributed to investments.</li>
                                        <li>New and existing plan/ product development.</li> <li>Strategic market research.</li> </ul> 
                                        <br>Do NOT include:<br/> <ul> 
                                        <li>Actuarial or legal advisors to the Board. These costs belong in 7c Actuarial and 7d Legal and Rule Interpretation, respectively.</li></ul>"
                />
                <SurveyElement
                  name='FTEBoard'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesBoard'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdBoard'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField33'
                  formula='SalariesBoard + ThirdBoard'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinBoard'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinBoard'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinBoard'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField36'
                  formula='MPinBoard + ITinBoard + OtherinBoard'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.4.c'
                  labelText='Government and Public Relations'
                  inputType={ElementType.LABEL}
                  helpText="<strong>Government and Public Relations</strong></br></br><ul>
                                        <li>Maintaining relationships with government, unions and employer organizations.</li> 
                                        <li>Anticipating, influencing and initiating plan rule changes by government, unions, employers, and employer organizations.</li> 
                                        <li>Costs of coordination with organizations of employers and employees.</li> 
                                        <li>Formulating a Governmental Policy document.</li> 
                                        <li>Media relations.</li>
                                        <li>Marketing costs to attract new employers, or new members, or to support the position of the pension 
                                        fund within the industry or other interest groups.</li> 
                                        <li>General communication/ marketing to support the position of the pension system in the community or in the industry.</li> 
                                        <li>Memberships in Councils of Industry Funds, Company Funds or International Organizations.</li></ul>"
                />
                <SurveyElement
                  name='FTEPR'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesPR'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdPR'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField34'
                  formula='SalariesPR + ThirdPR'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='MPinPR'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='ITinPR'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  inputType={ElementType.NUMBER}
                  name='OtherinPR'
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField37'
                  formula='MPinPR + ITinPR + OtherinPR'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow styleName='noHoverEffect'>
                <SurveyElement
                  labelText='<b>5. Major Projects </b>'
                  inputType={ElementType.LABEL}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.5.a'
                  labelText='Amortization of non-IT Major Projects '
                  inputType={ElementType.LABEL}
                  helpText="<strong>Amortization of non-IT Major Projects</strong></br>
                                        <br>Current year amortization of the non-IT portion of capitalized major projects.<br/>  
                                        <br>Major Projects are long lived assets that are capitalized (under GAAP or GASB 51) and expensed over their useful life, 
                                        which is greater than one reporting period. Examples of non-IT major projects could include if the costs were capitalized: 
                                        i) a large, one-time historic data cleaning project, or, ii) imaging all historic records.<br/>  
                                        <br>Do NOT include:<br/><ul> 
                                        <li>Amortization of the IT portion major projects. This belongs in activity 5c.</li> 
                                        <li>Amortization of leaseholds or office furniture. This belongs in activity 7a Building and Utilities.</li></ul>"
                />
                <SurveyElement />
                <SurveyElement />
                <SurveyElement
                  name='ThirdAmortNonIT'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField38'
                  formula='ThirdAmortNonIT'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.5.b'
                  labelText="Non-IT Major Projects (if you don' t capitalize)"
                  inputType={ElementType.LABEL}
                  helpText="<strong>Non-IT Major Projects (if you don't capitalize)</strong></br> 
                                        <br>If you do not capitalize major projects, then include the current year cost of the non-IT 
                                        portion of major projects that could be capitalized under GASB 51 or GAAP.<br/>  
                                        <br>Do NOT include the IT portion of major projects. 
                                        This belongs in activity 5d IT Major Projects (if you don't capitalize).<br/>"
                />
                <SurveyElement
                  name='FTEMPnotcapNonIT'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesMPnotcapNonIT'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdMPnotcapNonIT'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField39'
                  formula='SalariesMPnotcapNonIT + ThirdMPnotcapNonIT'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.5.c'
                  labelText="Amortization of IT Major Projects"
                  inputType={ElementType.LABEL}
                  helpText="<strong>Amortization of IT Major Projects</strong></br>
                                        <br>Current year amortization of the IT portion of capitalized major projects.<br/> 
                                        <br>Major Projects are long lived assets that are capitalized (under GAAP or GASB 51) 
                                        and expensed over their useful life, which is greater than one reporting period. 
                                        An example of an IT major project could be a major software development project in 
                                        the application stage of development, i.e., design, software configuration, software interfacing, 
                                        coding, installing hardware and testing.<br/>  
                                        <br>Do NOT include:<br/></ul> 
                                        <li>Amortization of the non-IT portion major projects. 
                                        This belongs in activity 5a.</li> 
                                        <li>Amortization/depreciation of IT infrastructure such as mainframes and servers. 
                                        This belongs in 6a IT Strategy, Database Management and Applications (excl. major projects).</li> 
                                        <li>Amortization/depreciation of desktop hardware and software, or telecommunications equipment. 
                                        This belongs in activity 6b IT Desktop, Network, and Telecommunication.</li> 
                                        <li>Amortization of projects that relate to investment management</li>"
                />
                <SurveyElement />
                <SurveyElement />
                <SurveyElement
                  name='ThirdAmortIT'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField40'
                  formula='ThirdAmortIT'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.5.d'
                  labelText="IT Major Projects (if you don't capitalize)"
                  inputType={ElementType.LABEL}
                  helpText="<strong>IT Major Projects (if you don't capitalize)</strong><br/> 
                                        <br>If you do not capitalize major projects, then include the current year cost of the IT portion of 
                                        major projects that could be capitalized under GASB 51 or GAAP.<br/>  <br>Do NOT include the non-IT 
                                        portion of major projects. This belongs in activity 5b.<br/>"
                />
                <SurveyElement
                  name='FTEMPnotcapIT'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesMPnotcapIT'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdMPnotcapIT'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField41'
                  formula='SalariesMPnotcapIT + ThirdMPnotcapIT'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow styleName='noHoverEffect'>
                <SurveyElement
                  labelText='<b>6. Information Technology</b>'
                  inputType={ElementType.LABEL}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.6.a'
                  labelText="IT Strategy, Database Management and Applications (excl. major projects)"
                  inputType={ElementType.LABEL}
                  helpText="<strong>IT Strategy, Database Management and Applications (excl. major projects and IT security)</strong><br/><br/>
                                        <ul><li>IT management and strategy.</li> 
                                        <li>IT architecture.</li> 
                                        <li>Data center i.e., current year cost (whether cash or amortization) of mainframe, servers, data center hosting.</li> 
                                        <li>Training.</li> 
                                        <li>Head of the IT department's office.</li> <li>Database management, including:  
                                        building databases, testing databases for data integrity, maintaining databases, ensuring operation of databases.</li> 
                                        <li>Application maintenance, enhancement and development that is not capitalized.</li> 
                                        <li>Project management that is not included in Major Projects.</li>
                                        <li>Disaster recovery.</li> </ul> 
                                        Do NOT include the portion of the above that relates to investment management.<br/>"
                />
                <SurveyElement
                  name='FTEITStrategy'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesITStrategy'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdITStrategy'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField42'
                  formula='SalariesITStrategy + ThirdITStrategy'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.6.b'
                  labelText="IT Desktop, Networks, Telecom"
                  inputType={ElementType.LABEL}
                  helpText="<strong>IT Desktop, Networks, Telecom (excl. IT security)</strong><br/><br> 
                                        <ul><li>User services including application licenses, personal computer hardware and software, help desk.</li> 
                                        <li>Network (i.e., LAN and  WAN).</li>
                                        <li>Telecommunications, including call center equipment and mobile devices. 
                                        If any of the above costs are capitalized, include current year amortization.</li> 
                                        </ul> Do NOT include the portion of the above that relates to investment management."
                />
                <SurveyElement
                  name='FTEITDesktop'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesITDesktop'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdITDesktop'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField43'
                  formula='SalariesITDesktop + ThirdITDesktop'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.6.c'
                  labelText="IT Security"
                  inputType={ElementType.LABEL}
                  helpText="<strong>IT Security</strong><br/><br/> <ul>
                                        <li>IT security management, strategy and reporting.</li>
                                        <li>Compliance with relevant rules and regulations about data privacy and security.</li>
                                        <li>Protecting data and information, and the systems used to process records and data, from unauthorized access or use.</li>
                                        <li>Testing security strategies and defenses.</li>
                                        <li>Maintaining, updating and developing new defensive systems and protocols.</li>
                                        <li>Detecting security incidents.</li>
                                        <li>Cyber insurance.</li><li>Includes network, application, endpoint, data, IT infrastructure, 
                                        cloud, identity management and database security.</li></ul>
                                        Do NOT include the portion of the above that relates to investment management."
                />
                <SurveyElement
                  name='FTEITSecurity'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesITSecurity'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdITSecurity'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField9'
                  formula='SalariesITSecurity + ThirdITSecurity'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow styleName='noHoverEffect'>
                <SurveyElement
                  labelText='<b>7. Support Services and Other</b>'
                  inputType={ElementType.LABEL}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.7.a'
                  labelText="Building and Utilities "
                  inputType={ElementType.LABEL}
                  helpText="<strong>Building and Utilities</strong><br/><br/>
                            <ul>
                                <li>Building occupancy costs including rent, lease, amortization of leaseholds and building and office furniture depreciation.</li>
                                <li>Building depreciation if you own the building.</li>
                                <li>Building insurance.</li>
                                <li>Building maintenance services such as custodial, maintenance, security services, etc.</li>
                                <li>Utilities.</li>
                            </ul>
                            Do NOT include the portion of the above that relates to investment management."
                />
                <SurveyElement
                  name='FTEBuilding'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesBuilding'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdBuilding'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField44'
                  formula='SalariesBuilding + ThirdBuilding'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.7.b'
                  labelText="Human Resources"
                  inputType={ElementType.LABEL}
                  helpText="<strong>Human Resources</strong><br/><br/>
                            <ul>
                              <li>Human resources staff and human resources consulting, including recruitment, training, career development, induction, disciplinary action etc.</li>
                              <li>Include time spent in the development of the HR framework, including policies and procedures.</li>
                            </ul>
                            Do NOT include:
                            <ul> 
                              <li>Time spent by non HR staff interviewing or training. For example the training of a call center agent belongs in activity 2a Call Center.</li> 
                              <li>The portion of the above that relates to investment management.</li> 
                              <li>Recruitment fees - these belong in the 'Third Party and Other Costs' of the activities where the FTE were hired.</li> 
                              <li>Separation or severance costs - these belong in the 'Third Party and Other Costs' of the activities that the terminated FTE performed.</li>
                            </ul>"
                />
                <SurveyElement
                  name='FTEHR'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesHR'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdHR'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField45'
                  formula='SalariesHR + ThirdHR'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.7.c'
                  labelText="Actuarial "
                  inputType={ElementType.LABEL}
                  helpText="<strong>Actuarial</strong><br/> 
                                        <br>All internal actuarial department costs, external actuarial fees and sister-entity cross charges for actuarial work including:<br/><ul> 
                                        <li>Actuarial work for financial and government reporting.</li> 
                                        <li>Actuarial work to determine funding policy, contribution rates or billing rates.</li> 
                                        <li>Quantifying the impact of changes in the plan.</li> <li>Advice on plan design / redesign, funding and contributions, 
                                        the employer's covenant, mergers and acquisitions, etc.</li> 
                                        <li>Inception, disability and healthcare calculations that require actuarial input.</li> 
                                        <li>Formulating an Actuarial Policy document.</li></ul>"
                />
                <SurveyElement
                  name='FTEActuarial'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesActuarial'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdActuarial'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField56'
                  formula='SalariesActuarial + ThirdActuarial'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.7.d'
                  labelText="Legal and Rule Interpretation"
                  inputType={ElementType.LABEL}
                  helpText="<strong>Legal and Rule Interpretation</strong><br/><br> <ul>
                                        <li>All external legal fees.</li> 
                                        <li>Internal legal department (including legal fees charged by sister organizations or the corporate parent)</li> 
                                        <li>Appeals concerning pension inceptions (as well as any other appeals) if they are handled by the legal department or by external legal counsel.</li> 
                                        <li>Plan contract amendments. Adding new participating employers.</li> 
                                        <li>Interpretation of existing rule sets and laws.</li> 
                                        <li>Developing rules that simplify the interpretation of the plan contract and related legislation on behalf of staff, employers or members.</li>  
                                        <li>Support to the communications department in writing brochures and other communication material.</li> </ul> 
                                        <br>Do NOT include the portion of the above that relates to investment management.<br/>"
                />
                <SurveyElement
                  name='FTELegal'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesLegal'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdLegal'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField48'
                  formula='SalariesLegal + ThirdLegal'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.7.e'
                  labelText="Internal and External Audit"
                  inputType={ElementType.LABEL}
                  helpText="<strong>Internal and External Audit</strong><br/><br> <ul>
                                        <li>Fees paid to the external auditor, particularly for the audit opinion on the accounts.</li> 
                                        <li>The external auditor may be the public audit office or an independent audit firm - in either case these fees or charges belong in  
                                        the '3rd Party Fees' column.</li> <li>The costs of internal audit and/or quality assurance departments.</li></ul>"
                />
                <SurveyElement
                  name='FTEAudit'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesAudit'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdAudit'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField49'
                  formula='SalariesAudit + ThirdAudit'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.7.f'
                  labelText="Pay-as-you-go benefits for retired staff"
                  inputType={ElementType.LABEL}
                  helpText="<strong>Pay-as-you-go Benefits for Retired Staff</strong><br/><br/>
                                        All pension and other post employment benefits (OPEB) such as healthcare paid in the current year for 
                                        retired administrative staff. Exclude amounts that relate to retired investment staff."
                />
                <SurveyElement />
                <SurveyElement
                  name='SalariesPayAsYouGo'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$ or n/a"
                  allowNA
                />
                <SurveyElement />
                <SurveyElement
                  name='calculatedField50'
                  formula='SalariesPayAsYouGo'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  questionNumber='10.7.g'
                  labelText="Other Support Services"
                  inputType={ElementType.LABEL}
                  helpText="<strong>Other Support Services</strong><br/> <br>
                                        All other pension administration costs that have not been included elsewhere."
                />
                <SurveyElement
                  name='FTEOtherSupport'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='SalariesOtherSupport'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='ThirdOtherSupport'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
                <SurveyElement
                  name='calculatedField65'
                  formula='SalariesOtherSupport + ThirdOtherSupport'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='<b>Total Benefit Administration Expenses</b>'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='fteTotal'
                  formula='FTEITSecurity + FTEPaying + FTEInceptions_InclEstimates + 
                                        FTERefunds + FTEPurchases + FTEDisabilityB + FTEHealthcare + FTEOptional + FTECalls + FTEMail + 
                                        FTECounseling1on1 + FTECounselingGroup + FTEEstimates + FTECommunication + FTEDataAndMoney + 
                                        FTEEmployers + FTEDataMembers + FTEFinancial + FTEBoard + FTEPR + FTEMPnotcapNonIT + 
                                        FTEMPnotcapIT + FTEITStrategy + FTEITDesktop + FTEBuilding + FTEHR + FTEActuarial + FTELegal + 
                                        FTEAudit + FTEOtherSupport'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='salaryTotal'
                  formula='SalariesITSecurity + SalariesPaying + SalariesInceptions_InclEstimates + SalariesRefunds + 
                                        SalariesPurchases + SalariesDisabilityB + SalariesHealthcare + SalariesOptional + SalariesCalls + 
                                        SalariesMail + SalariesCounseling1on1 + SalariesCounselingGroup + SalariesEstimates + SalariesCommunication + 
                                        SalariesDataAndMoney + SalariesEmployers + SalariesDataMembers + SalariesFinancial + SalariesBoard + SalariesPR + 
                                        SalariesMPnotcapNonIT + SalariesMPnotcapIT + SalariesITStrategy + SalariesITDesktop + SalariesBuilding + 
                                        SalariesHR + SalariesActuarial + SalariesLegal + SalariesAudit + SalariesPayAsYouGo + SalariesOtherSupport'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='thirdPartyTotal'
                  formula='ThirdITSecurity +  ThirdPaying + ThirdInceptions_InclEstimates + ThirdRefunds + ThirdPurchases + 
                                        ThirdDisabilityB + ThirdHealthcare + ThirdOptional + ThirdCalls + ThirdMail + ThirdCounseling1on1 + 
                                        ThirdCounselingGroup + ThirdEstimates + ThirdCommunication + ThirdDataAndMoney + ThirdEmployers + 
                                        ThirdDataMembers + ThirdFinancial + ThirdBoard + ThirdPR + ThirdAmortNonIT + ThirdMPnotcapNonIT + 
                                        ThirdAmortIT + ThirdMPnotcapIT + ThirdITStrategy + ThirdITDesktop  +ThirdBuilding + ThirdHR + ThirdActuarial + 
                                        ThirdLegal + ThirdAudit + ThirdOtherSupport'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  name='directCostTotal'
                  formula='SalariesITSecurity + ThirdITSecurity + calculatedField14 + calculatedField5 + calculatedField50 + 
                                        calculatedField49 + calculatedField48 + calculatedField56 + calculatedField45 + calculatedField44 + 
                                        calculatedField43 + calculatedField42 + calculatedField41 + calculatedField40 + calculatedField39 + 
                                        calculatedField38 + calculatedField34 + calculatedField33 + calculatedField32 + calculatedField28 + 
                                        calculatedField27 + calculatedField26 + calculatedField19 + calculatedField18 + calculatedField17 + 
                                        calculatedField16 + calculatedField15  + calculatedField7 + calculatedField6 + calculatedField4 + 
                                        calculatedField3 + calculatedField46 + total1 + calculatedField65'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='majorTotal'
                  formula='MPinPaying + MPinInceptions + MPinRefunds + MPinPurchases +  MPinDisabilityB + MPinHealthcare +
                                        MPinOptional + MPinCalls + MPinMail + MPinCounseling1on1 + MPinCounselingGroup + MPinEstimates + 
                                        MPinCommunication + MPinDataAndMoney + MPinEmployers + MPinDataMembers + MPinFinancial + MPinBoard + MPinPR'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='itTotal'
                  formula='ITinPaying + ITinInceptions + ITinRefunds + ITinPurchases + ITinDisabilityB + ITinHealthcare + 
                                        ITinOptional + ITinCalls + ITinMail + ITinCounseling1on1 + ITinCounselingGroup + ITinEstimates + 
                                        ITinCommunication + ITinDataAndMoney + ITinEmployers + ITinDataMembers + ITinFinancial + ITinBoard + ITinPR '
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='optionalTotal'
                  formula='OtherinPaying + OtherinInceptions + OtherinRefunds + OtherinPurchases + OtherinDisabilityB + 
                                        OtherinHealthcare + OtherinOptional + OtherinCalls + OtherinMail + OtherinCounseling1on1 +
                                        OtherinCounselingGroup + OtherinEstimates + OtherinCommunication + OtherinDataAndMoney + 
                                        OtherinEmployers + OtherinDataMembers + OtherinFinancial + OtherinBoard + OtherinPR'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
                <SurveyElement
                  hidden={!isOptionalTable}
                  name='calculatedField59'
                  formula='calculatedField8 + calculatedField9_ + calculatedField10 + calculatedField11 + calculatedField12 + 
                                        calculatedField13 + calculatedField144 + calculatedField20 + calculatedField21 + calculatedField22 + 
                                        calculatedField23 + calculatedField24 + calculatedField25 + calculatedField29 + calculatedField30 + 
                                        calculatedField31 + calculatedField35 + calculatedField36 + calculatedField37'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='FTE relating to major projects capitalized, if any, during the fiscal year'
                  inputType={ElementType.LABEL}
                  helpText="FTE relating to major projects capitalized during the fiscal year will not be included in the 
                                        total immediately above because their salaries are being capitalized instead of expensed. But both the FTE and their salaries will be part 
                                        the 'cash' budget for the fiscal year and it is necessary to include them in order to understand the total FTE working for administration 
                                        during the fiscal year."
                />
                <SurveyElement
                  name='FTECapitalized'
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='Total Administration FTE'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='calculatedField60'
                  formula='fteTotal + FTECapitalized'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
            </SurveyGridBody>
          </SurveyElement>
          <SurveyElement
            questionNumber="11"
            labelText="What percentage of total salaries and benefits costs (per the table immediately above) relates to benefits?"
            name="SalariesBenefitsPct"
            inputType={ElementType.NUMBER}
            format={NumberFormat.PERCENTAGE}
          />
          <SurveyElement
            questionNumber="12"
            labelText="Please provide the following information about your core pension administration system:"
            inputType=""
            name=""
          />
          <SurveyElement
            questionNumber="12.a"
            labelText="For how many years has this system been in live, day-to-day use?"
            name="AdminSystemAgeYears"
            inputType={ElementType.NUMBER}
            format={NumberFormat.DECIMAL1}
            placeholder="# of years"
            subQuestion
            allowNA
            allowUnknown
          />
          <SurveyElement
            questionNumber="12.b"
            labelText="Was the system originally developed in-house or by a third-party provider?"
            name="AdminSystemInHouseThirdParty"
            inputType={ElementType.SELECT}
            options={SelectCombosPABS.INHOUSE_THIRD_BOTH}
            saveAsKey={true}
            subQuestion
          />
          <SurveyConditionalSegment
            showIf={{
              AdminSystemInHouseThirdParty: { not: "inhouse" }
            }}
          >
            <SurveyElement
              questionNumber="12.b.b1"
              labelText="If developed by a third party vendor: Approximately what percentage of the system was customized for your organization?"
              name="AdminSystemCustomization%"
              inputType={ElementType.NUMBER}
              format={NumberFormat.PERCENTAGE}
              doubleSubQuestion
              allowNA
              allowUnknown
            />

          </SurveyConditionalSegment>
          <SurveyElement
            questionNumber="13"
            labelText="Do you expect to replace your core pension administration system within the next 10 years?"
            note="Replace means implementing an entirely new pension administration system to replace the legacy platform, whether built in-house or purchased from a third party. Upgrade means enhancing or patching the existing system (updates, modules, improvements) without replacing the underlying platform."
            name="AdminSystemReplaceFutureYN"
            inputType={ElementType.SELECT}
            options={SelectCombos.YES_NO}
          />
            <SurveyElement
              questionNumber="14"
              labelText="Is your organization currently in the process of replacing the core pension administration system?"
              note="Answer 'no' if you continually upgrade your pension administration system, so you won't have to undergo a major systems modernization project in the future."
              inputType={ElementType.SELECT}
              options={SelectCombos.YES_NO}
              name="AdminSystemReplaceNowYN"
            />
            <SurveyConditionalSegment
              showIf={{
                AdminSystemReplaceNowYN: { not: CommonResponses.NO }
              }}
            >
              <SurveyElement
                questionNumber="14.a"
                labelText="How many years has the replacement project been underway?"
                inputType={ElementType.NUMBER}
                format={NumberFormat.DECIMAL1}
                name="AdminSystemOngoingYears"
                placeholder="# of years or n/a"
                subQuestion
                allowNA
                allowUnknown
              />
              <SurveyElement
                questionNumber="14.b"
                labelText="In how many years do you expect the new system to go live?"
                inputType={ElementType.NUMBER}
                format={NumberFormat.DECIMAL1}
                name="AdminSystemOngoingLiveYears"
                placeholder="# of years or n/a"
                subQuestion
                allowNA
                allowUnknown
              />
            </SurveyConditionalSegment>
          <SurveyElement
            questionNumber="15"
            labelText="Did you have any material legislative changes or other unusual events that materially affected your costs and/ or service in the most recent fiscal year?"
            inputType={ElementType.SELECT}
            options={SelectCombos.YES_NO}
            name="Q_247"
          />
          <SurveyElement
            questionNumber="15.a"
            labelText="If yes, describe:"
            inputType={ElementType.TEXTAREA}
            name="Q_247aDescr"
            showIf={{Q_247: {not: CommonResponses.NO}}}
            subQuestion
            allowUnknown
          />
          <SurveyElement
            questionNumber="16"
            labelText="If you do not capitalized major projects, provide details of the projects included in activities 5b and 5d per the table above."
          />
          <SurveyElement inputType='grid' styleName={'customTablePabs'}>
            <SurveyGridHeader columnWidths={['5%', '60%', '35%']}>
              <SurveyElement
                labelText=''
                inputType={ElementType.LABEL}
              />
              <SurveyElement
                labelText='Description of 5b Non-IT Major Projects	'
                inputType={ElementType.LABEL}
              />
              <SurveyElement
                labelText='Cost ($000s)'
                inputType={ElementType.LABEL}
              />
            </SurveyGridHeader>

            <SurveyGridBody>
              <SurveyGridRow>
                <SurveyElement
                  labelText='a.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  labelText=''
                  name='Q_16a'
                  inputType={ElementType.TEXT}
                />
                <SurveyElement
                  name="Q_16aCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='b.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_17a'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_17aCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='c.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_18a'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_18aCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='d.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_19a'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_19aCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='e.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_20a'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_20aCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='f.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_21a'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_21aCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='g.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_22a'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_22aCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='h.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_23a'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_23aCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='i.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_24a'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_24aCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText=''
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  labelText='Total'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='calculatedField47'
                  formula='Q_16aCost + Q_17aCost + Q_18aCost + Q_19aCost + Q_20aCost + Q_21aCost + Q_22aCost + Q_23aCost + Q_24aCost'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
            </SurveyGridBody>
          </SurveyElement>
          <SurveyElement inputType='grid' styleName={'customTablePabs'}>
              <SurveyGridHeader columnWidths={['5%', '60%', '35%']}>
              <SurveyElement
                labelText=''
                inputType={ElementType.LABEL}
              />
              <SurveyElement
                labelText='Description of 5d IT Major Projects'
                inputType={ElementType.LABEL}
              />
              <SurveyElement
                labelText='Cost ($000s)'
                inputType={ElementType.LABEL}
              />
            </SurveyGridHeader>
            <SurveyGridBody>
              <SurveyGridRow>
                <SurveyElement
                  labelText='a.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  labelText=''
                  name='Q_16f'
                  inputType={ElementType.TEXT}
                />
                <SurveyElement
                  name="Q_16fCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='b.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_17f'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_17fCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='c.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_18f'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_18fCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='d.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_19f'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_19fCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='e.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_20f'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_20fCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='f.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_21f'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_21fCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='g.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_22f'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_22fCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='h.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_23f'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_23fCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText='i.'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='Q_24f'
                  inputType={ElementType.TEXT}
                  allowNA
                />
                <SurveyElement
                  name="Q_24fCost"
                  inputType={ElementType.NUMBER}
                  format={NumberFormat.DECIMAL1}
                  placeholder="$"
                />
              </SurveyGridRow>
              <SurveyGridRow>
                <SurveyElement
                  labelText=''
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  labelText='Total'
                  inputType={ElementType.LABEL}
                />
                <SurveyElement
                  name='calculatedField51'
                  formula='Q_16fCost + Q_17fCost + Q_18fCost + Q_19fCost + Q_20fCost + Q_21fCost + Q_22fCost + Q_23fCost + Q_24fCost'
                  inputType={ElementType.CALCULATED}
                  format={NumberFormat.DECIMAL1}
                />
              </SurveyGridRow>
            </SurveyGridBody>
          </SurveyElement>
        </SurveySection>
      </SurveyContentContainer>
    </SurveyPage>
  );
};